<template>
  <div
    class="relative w-full slider-container"
    :class="{ 'flex items-center gap-4': !arrowsInternal }"
  >
    <!-- Container principale -->
    <div class="relative flex-1 overflow-hidden">
      <!-- Freccia interna sinistra -->
      <button
        v-if="arrowsInternal && !loading && canScrollLeft"
        @click="scrollLeft"
        class="flex items-center justify-center hover:bg-opacity-100 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed slider-arrow outline-none focus:outline-none absolute left-0 top-0 transform -translate-y-1/2 z-10"
        :disabled="!canScrollLeft"
        style="top: 50%"
      >
        <div
          class="hover:text-illinois-text text-illinois-text-muted transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed h-full w-full flex items-center justify-center"
          :class="[
            showArrowsBg
              ? 'arrows-internal bg-illinois-darker bg-opacity-75'
              : 'w-10 h-10 '
          ]"
        >
          <IllinoisIcons icon="chevron-left" :width="20" :height="16" />
        </div>
      </button>

      <div
        ref="slider"
        class="flex overflow-x-auto slider-scroll"
        @scroll.passive="onScroll"
        style="scroll-behavior: smooth; scrollbar-width: none"
        :style="{
          paddingLeft: edgeOffset + 'px',
          paddingRight: edgeOffset + 'px',
          scrollPaddingLeft: edgeOffset + 'px',
          scrollPaddingRight: edgeOffset + 'px',
          gap: gutter + 'px'
        }"
      >
        <template v-if="!loading">
          <slot></slot>
        </template>
        <template v-else>
          <div
            v-for="idx in skeletonCount"
            :key="idx"
            class="rounded bg-illinois-white-20 shimmer text-illinois-white-40"
            :style="{
              width: skeletonItemWidth + 'px',
              height: skeletonItemHeight + 'px'
            }"
          ></div>
        </template>
      </div>

      <!-- Freccia interna destra -->
      <button
        v-if="arrowsInternal && !loading && canScrollRight"
        @click="scrollRight"
        class="flex items-center justify-center hover:bg-opacity-100 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed slider-arrow outline-none focus:outline-none absolute right-0 transform -translate-y-1/2 z-10"
        :disabled="!canScrollRight"
        style="top: 50%"
      >
        <div
          class="hover:text-illinois-text text-illinois-text-muted transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed h-full w-full flex items-center justify-center"
          :class="[
            showArrowsBg
              ? 'arrows-internal bg-illinois-darker bg-opacity-75'
              : 'w-10 h-10'
          ]"
        >
          <IllinoisIcons icon="chevron-right" :width="18" :height="18" />
        </div>
      </button>
    </div>

    <!-- Freccia esterna sinistra -->
    <button
      v-if="!arrowsInternal && !loading && canScrollLeft"
      @click="scrollLeft"
      class="flex items-center justify-center bg-illinois-darker hover:bg-opacity-100 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed slider-arrow outline-none focus:outline-none absolute left-0 top-0 transform -translate-y-1/2 z-10 -translate-x-8"
      :disabled="!canScrollLeft"
      style="top: 50%"
    >
      <div
        class="hover:text-illinois-text text-illinois-text-muted transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed h-full w-full flex items-center justify-center w-10 h-10"
      >
        <IllinoisIcons icon="chevron-left" :width="20" :height="16" />
      </div>
    </button>

    <!-- Freccia esterna destra -->
    <button
      v-if="!arrowsInternal && !loading && canScrollRight"
      @click="scrollRight"
      class="flex items-center justify-center hover:bg-opacity-100 text-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed slider-arrow outline-none focus:outline-none absolute right-0 transform -translate-y-1/2 z-10 translate-x-8"
      :disabled="!canScrollRight"
      style="top: 50%"
    >
      <div
        class="hover:text-illinois-text text-illinois-text-muted transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed h-full w-full flex items-center justify-center w-10 h-10"
      >
        <IllinoisIcons icon="chevron-right" :width="18" :height="18" />
      </div>
    </button>
  </div>
</template>

<script>
import IllinoisIcons from './icons.vue';

export default {
  name: 'IllinoisSlider',
  components: {
    IllinoisIcons
  },
  props: {
    arrowsInternal: {
      type: Boolean,
      default: true
    },
    scrollAmount: {
      type: Number,
      default: 300
    },
    showArrowsBg: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    skeletonCount: {
      type: Number,
      default: 4
    },
    skeletonItemWidth: {
      type: Number,
      default: 340
    },
    skeletonItemHeight: {
      type: Number,
      default: 160
    },
    edgeOffset: {
      type: Number,
      default: 20
    },
    gutter: {
      type: Number,
      default: 16
    }
  },
  data() {
    return {
      canScrollLeft: false,
      canScrollRight: true
    };
  },
  mounted() {
    this.updateScrollState();
    // Aggiorna lo stato quando il contenuto cambia
    this.$nextTick(() => {
      this.updateScrollState();
    });
  },
  methods: {
    onScroll() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this.updateScrollState();
      });
    },
    scrollLeft() {
      const slider = this.$refs.slider;
      slider.scrollBy({
        left: -this.scrollAmount,
        behavior: 'smooth'
      });
    },
    scrollRight() {
      const slider = this.$refs.slider;
      slider.scrollBy({
        left: this.scrollAmount,
        behavior: 'smooth'
      });
    },
    updateScrollState() {
      const slider = this.$refs.slider;
      if (!slider) return;

      this.canScrollLeft = slider.scrollLeft > 0;
      this.canScrollRight =
        slider.scrollLeft < slider.scrollWidth - slider.clientWidth;
    }
  }
};
</script>

<style scoped>
/* Nasconde completamente la scrollbar */
.slider-scroll::-webkit-scrollbar {
  display: none;
}

.slider-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}

/* Scroll snap behavior */
.slider-scroll {
  scroll-snap-type: x mandatory;
  -webkit-scroll-snap-type: x mandatory;
}

.slider-scroll > * {
  scroll-snap-align: start;
  -webkit-scroll-snap-align: start;
  flex-shrink: 0;
}

/* Nasconde le frecce di default */
.slider-arrow {
  opacity: 0;
  transition: opacity 0.2s ease-in-out;
}

/* Mostra le frecce solo al hover sull'intero slider */
.slider-container:hover .slider-arrow {
  opacity: 1;
}

/* Effetto scale solo sull'icona al hover del bottone */
.slider-arrow svg {
  transition: transform 0.2s ease !important;
  transform: scale(1) !important;
}

.slider-arrow:hover svg {
  transform: scale(1.5) !important;
}

.slider-arrow div svg {
  transition: transform 0.2s ease !important;
  transform: scale(1) !important;
}

.slider-arrow:hover div svg {
  transform: scale(1.5) !important;
}

.arrows-internal {
  width: 30px;
  height: 50px;
  border-radius: 0;
}
</style>
